import { useEffect, useState } from 'react'
import { fetchLive, type LiveSession } from '../../lib/api'
import { stepLabel } from '../stepLabels'

const POLL_INTERVAL_MS = 5000

const AUDIENCE_LABEL: Record<LiveSession['audience'], string> = {
  unge: 'Unge',
  aeldre: 'Ældre',
}
const VARIANT_LABEL: Record<LiveSession['variant'], string> = {
  chat: 'Chat',
  calculator: 'Beregner',
}

function fmtSeconds(s: number): string {
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const r = s % 60
  return r === 0 ? `${m}m` : `${m}m ${r}s`
}

export function LiveSessions() {
  const [sessions, setSessions] = useState<LiveSession[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    const load = () => {
      fetchLive()
        .then((r) => { if (!cancelled) { setSessions(r.sessions); setError(null) } })
        .catch((e) => { if (!cancelled) setError(String(e)) })
    }
    load()
    const id = setInterval(load, POLL_INTERVAL_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // Re-render every second so the "X sekunder siden" countdown stays fresh
  // mellem poll-intervaller (poll = 5s, men relativ tid skal opdateres oftere)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const count = sessions?.length ?? 0
  const byFlow = new Map<string, number>()
  if (sessions) {
    for (const s of sessions) {
      const key = `${s.variant}:${s.audience}`
      byFlow.set(key, (byFlow.get(key) ?? 0) + 1)
    }
  }

  return (
    <div className="card live-card">
      <div className="card-header live-header">
        <div>
          <div className="card-title">
            <span className={`live-dot${count > 0 ? ' live-dot-active' : ''}`} aria-hidden /> Live visning
          </div>
          <div className="card-subtitle">
            {count === 0
              ? 'Ingen brugere er aktive lige nu'
              : `${count} ${count === 1 ? 'bruger' : 'brugere'} aktiv${count === 1 ? '' : 'e'} lige nu — opdateres hvert ${POLL_INTERVAL_MS / 1000}. sekund`}
          </div>
        </div>
        {count > 0 && (
          <div className="live-flow-pills">
            {[...byFlow.entries()]
              .sort((a, b) => b[1] - a[1])
              .map(([key, n]) => {
                const [variant, audience] = key.split(':')
                return (
                  <span key={key} className="live-flow-pill">
                    {VARIANT_LABEL[variant as LiveSession['variant']]} – {AUDIENCE_LABEL[audience as LiveSession['audience']]}
                    <strong>{n}</strong>
                  </span>
                )
              })}
          </div>
        )}
      </div>

      {error && <div className="muted small" style={{ color: 'var(--danger, #f87171)' }}>{error}</div>}

      {sessions === null ? (
        <div className="muted small">Indlæser…</div>
      ) : sessions.length === 0 ? null : (
        <div className="live-list" data-tick={tick}>
          {sessions.map((s) => (
            <div key={s.id} className="live-row">
              <div className="live-row-flow">
                <span className={`pill audience-${s.audience}`}>{AUDIENCE_LABEL[s.audience]}</span>
                <span className={`pill ${s.variant}`}>{VARIANT_LABEL[s.variant]}</span>
              </div>
              <div className="live-row-step">
                <span className="live-step-label">{stepLabel(s.variant, s.lastStep, s.audience)}</span>
                <span className="live-step-id muted small">{s.lastStep}</span>
              </div>
              <div className="live-row-meta muted small">
                {fmtSeconds(s.secondsAgo)} siden · {fmtSeconds(s.secondsInFlow)} i flow
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
